/**
 * Text-only entry point for /home (PromptView).
 *
 * Sits between the ImageStartCard and the IntakeForm as a third "way in":
 * the user types a freeform description, presses Build, and we send that
 * straight as the first chat message — no intake form to fill, no image
 * to analyze. The agent reads the text and auto-detects category server-side.
 *
 * Kept deliberately small: a single textarea + a Build button. Detailed
 * structured input (size, material, fastening) belongs in IntakeForm.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Loader2, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

const MIN_LENGTH = 10;
const MAX_LENGTH = 2000;

interface Props {
  /** Called when the user clicks "Build it" — parent writes the
   *  conversation doc, sends the first chat message with this text,
   *  and navigates to the editor. */
  onBuild: (text: string) => void | Promise<void>;
  /** Locks the card while parent is starting the build. */
  isBuilding: boolean;
}

export function TextStartCard({ onBuild, isBuilding }: Props) {
  const { t } = useTranslation('prompt');
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const tooShort = trimmed.length < MIN_LENGTH;
  const disabled = isBuilding || tooShort;

  const handleBuild = () => {
    if (disabled) return;
    void onBuild(trimmed);
  };

  return (
    <div className="flex w-full flex-col gap-3 rounded-lg border-2 border-brand-indigo/30 bg-k-card p-3 text-sm transition-colors hover:border-brand-indigo/60">
      <div className="flex items-center gap-2 text-brand-indigo">
        <Pencil className="h-4 w-4" />
        <span className="text-xs font-bold uppercase tracking-wider">
          {t('textStart.headline', {
            defaultValue: 'Or just describe what you want',
          })}
        </span>
      </div>
      <p className="text-xs text-k-ink/70">
        {t('textStart.subline', {
          defaultValue:
            "Type a sentence or two. Include size, material, and what it's for if you can. We'll start building from your description.",
        })}
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX_LENGTH))}
        onKeyDown={(e) => {
          if (
            (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) &&
            !disabled
          ) {
            e.preventDefault();
            handleBuild();
          }
        }}
        disabled={isBuilding}
        rows={3}
        placeholder={t('textStart.placeholder', {
          defaultValue:
            'e.g. A 1200×800×720mm oak dining table with four square tapered legs.',
        })}
        className="w-full resize-y rounded-md border border-brand-indigo/20 bg-white p-2 text-xs text-k-ink placeholder:text-k-ink/40 focus:border-brand-indigo focus:outline-none focus:ring-1 focus:ring-brand-indigo disabled:cursor-not-allowed disabled:opacity-60"
      />
      <div className="flex items-center justify-between gap-3">
        <span
          className={cn(
            'text-[10px] tabular-nums',
            tooShort
              ? 'text-k-ink/40'
              : trimmed.length > MAX_LENGTH - 100
                ? 'text-amber-600'
                : 'text-brand-indigo/60',
          )}
        >
          {trimmed.length}/{MAX_LENGTH}
        </span>
        <button
          type="button"
          onClick={handleBuild}
          disabled={disabled}
          className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand-indigo bg-brand-indigo px-4 py-2 text-xs font-bold text-brand-cream transition-colors hover:bg-brand-indigo-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isBuilding ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>
                {t('textStart.buildingButton', {
                  defaultValue: 'Starting…',
                })}
              </span>
            </>
          ) : (
            <>
              <span>
                {t('textStart.buildButton', {
                  defaultValue: 'Build it from this',
                })}
              </span>
              <ArrowRight className="h-3.5 w-3.5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
